<template>
    <DFormRow
        :label="__('customers.customers.business_id')"
        :error="errors.business_id || businessIdError"
    >
        <div class="business-id-finland-container">
            <DInput
                id="business_id"
                v-model="form.business_id"
                :placeholder="__('customers.customers.business_id')"
                :is-valid="isFormFieldValid('business_id') || businessIdError === ''"
                maxlength="13"
            />
            <DButton
                id="business-id-check"
                class="business-id-check"
                :label="__('customers.customers.ssn_business_id_check_button')"
                color="primary"
                @click="checkBusinessIdField"
            />
        </div>
    </DFormRow>
    <DModal
        :show="isModalShown"
        :title="__('customers.customers.business_id_check_results')"
        @close="closeModal"
    >
        <DTable
            :fields="fields"
            :items="tableData"
        >
            <template #company_name="{item}">
                <td> {{ item.company_name }} </td>
            </template>
            <template #address="{item}">
                <td> {{ item.address }} </td>
            </template>
        </DTable>
    </DModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { route } from '~@/Composables/route'

import { useForm } from '@inertiajs/vue3'
import { __ } from '~@/Composables/translation'

const emit = defineEmits(['business-id-value'])

const props = defineProps({
    errors: {
        type: Object,
        default: () => { return {} }
    },
    businessId: {
        type: String,
        default: () => { return '' }
    }
})

const form = useForm({
    business_id: props.businessId
})

const businessIdError = ref('')
const isModalShown = ref(false)
const fields = ref([
    { key: 'company_name', label: __('customers.customers.company_name') },
    { key: 'address', label: __('customers.customers.address') }
])
const tableData = ref([])
const businessIdSync = computed(() => form.business_id)

function checkBusinessIdField () {
    window.axios.post(route('customers.customers.get_business_id_data', form))
        .then((response) => {
            const responseData = JSON.parse(response.data.business_id_data)

            setBusinessIdErrorMessage('')

            if (isNoneOrMoreThanOneCompany(responseData)) {
                setBusinessIdErrorMessage(__('customers.customers.business_id_check_error'))
                return
            }

            showCompanyDetailsInModal(responseData)
        }).catch(function (error) {
            setBusinessIdErrorMessage(error.response?.data?.message || 'Internal server error.')
        })
}
function closeModal () {
    isModalShown.value = false
}

function showModal () {
    isModalShown.value = true
}

function isNoneOrMoreThanOneCompany (responseData) {
    return responseData.totalResults !== 1
}

function showCompanyDetailsInModal (responseData) {
    const companyName = getCompanyName(responseData)
    const address = getCompanyAddress(responseData)

    setTableData(companyName, address)
    showModal()
}

function getCompanyName (responseData) {
    return responseData.companies[0].names
        .filter(item => item.type === '1' && !item.endDate)
        .map(item => item.name)[0]
}

function getCompanyAddress (responseData) {
    const filteredAddresses = responseData.companies[0].addresses
        .filter(address => address.type === 2)
        .map(address => ({
            ...address,
            postOffices: address.postOffices
                .filter(postOffice => postOffice.languageCode === '1')
        }))

    if (filteredAddresses.length === 0) {
        return '/'
    }

    const { buildingNumber, street, postCode, postOffices } = filteredAddresses[0]
    const city = postOffices[0]?.city || ''

    const cleanAddressParts = [buildingNumber, street, postCode, city].filter(part => part)

    return cleanAddressParts.join(' ')
}

function setTableData (companyName, address) {
    tableData.value = [
        { company_name: companyName, address: address }
    ]
}

function setBusinessIdErrorMessage (message) {
    businessIdError.value = message
}

watch(businessIdSync, (newValue) => {
    emit('business-id-value', newValue)
})
</script>

<style scoped lang="scss">
.business-id-finland-container {
    display: flex;
    justify-content: center;
    align-items: center;
    .business-id-check {
        margin-left: 10px;
    }
}
</style>
